import { IContentBlockSchemaItem, JsonSchemaField } from '@/models/content-block-schema.model';
import { IContentPage } from '@/models/content-page.model';
import { SupportedLocales } from '@/models/localization.model';
import { getInspectorBlock } from './blockInspectorLocaleMerge';
import { getArrayItemFieldMap, normalizeRootSchema } from './schemaDefaults';
import { isValidHttpOrHttpsUrl } from './isValidHttpOrHttpsUrl';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuid(s: string): boolean {
  return UUID_RE.test(s.trim());
}

function isLocaleStringMap(v: unknown, locales: SupportedLocales[]): v is Record<string, string> {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  const keys = Object.keys(o);
  if (keys.length === 0) return false;
  return keys.every((k) => locales.includes(k as SupportedLocales) && typeof o[k] === 'string');
}

function isLocaleStringArrayMap(v: unknown, locales: SupportedLocales[]): v is Record<string, string[]> {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  const keys = Object.keys(o);
  if (keys.length === 0) return false;
  return keys.every(
    (k) =>
      locales.includes(k as SupportedLocales) &&
      Array.isArray(o[k]) &&
      (o[k] as unknown[]).every((x) => typeof x === 'string'),
  );
}

function isObjectMapItems(
  items: JsonSchemaField | Record<string, JsonSchemaField> | undefined,
): items is Record<string, JsonSchemaField> {
  return !!items && typeof items === 'object' && !('type' in items && (items as JsonSchemaField).type !== undefined);
}

export type TContentBlockValidationError = {
  messageKey: string;
  values?: Record<string, string | number>;
  blockId?: string;
};

function err(
  messageKey: string,
  values: Record<string, string | number>,
  blockId?: string,
): TContentBlockValidationError {
  return { messageKey, values, blockId };
}

function validateTranslatableString(
  schema: JsonSchemaField,
  value: unknown,
  fieldPath: string,
  blockLabel: string,
  locales: SupportedLocales[],
  blockId?: string,
): TContentBlockValidationError | null {
  const required = schema.required === true;

  if (!required && (value === undefined || value === null)) {
    return null;
  }

  if (required) {
    if (!isLocaleStringMap(value, locales)) {
      const loc = locales[0];
      return err(
        'validation_block_field_locale_required',
        { block: blockLabel, field: fieldPath, locale: (loc ?? 'UK').toUpperCase() },
        blockId,
      );
    }
    const m = value as Record<string, string>;
    for (const loc of locales) {
      const s = (m[loc] ?? '').trim();
      if (!s) {
        return err(
          'validation_block_field_locale_required',
          { block: blockLabel, field: fieldPath, locale: loc.toUpperCase() },
          blockId,
        );
      }
      if (schema.max !== undefined && s.length > schema.max) {
        return err(
          'validation_block_max_length',
          {
            block: blockLabel,
            field: fieldPath,
            max: schema.max,
            localeHint: ` \u2014 ${loc.toUpperCase()}`,
          },
          blockId,
        );
      }
      if (schema.format === 'url' && !isValidHttpOrHttpsUrl(s)) {
        return err(
          'validation_block_invalid_url',
          { block: blockLabel, field: fieldPath, localeHint: ` \u2014 ${loc.toUpperCase()}` },
          blockId,
        );
      }
    }
    return null;
  }

  if (value === undefined || value === null) {
    return null;
  }
  if (!isLocaleStringMap(value, locales)) {
    return null;
  }
  const m = value as Record<string, string>;
  for (const loc of locales) {
    const s = (m[loc] ?? '').trim();
    if (schema.max !== undefined && s.length > schema.max) {
      return err(
        'validation_block_max_length',
        {
          block: blockLabel,
          field: fieldPath,
          max: schema.max,
          localeHint: ` \u2014 ${loc.toUpperCase()}`,
        },
        blockId,
      );
    }
    if (s && schema.format === 'url' && !isValidHttpOrHttpsUrl(s)) {
      return err(
        'validation_block_invalid_url',
        { block: blockLabel, field: fieldPath, localeHint: ` \u2014 ${loc.toUpperCase()}` },
        blockId,
      );
    }
  }
  return null;
}

function validatePlainString(
  schema: JsonSchemaField,
  value: unknown,
  fieldPath: string,
  blockLabel: string,
  blockId?: string,
): TContentBlockValidationError | null {
  const required = schema.required === true;

  if (!required && (value === undefined || value === null || value === '')) {
    return null;
  }

  if (typeof value !== 'string') {
    if (required) {
      return err('validation_block_field_required', { block: blockLabel, field: fieldPath }, blockId);
    }
    return null;
  }

  const s = value.trim();
  if (required && !s) {
    return err('validation_block_field_required', { block: blockLabel, field: fieldPath }, blockId);
  }

  if (schema.max !== undefined && s.length > schema.max) {
    return err(
      'validation_block_max_length',
      { block: blockLabel, field: fieldPath, max: schema.max, localeHint: '' },
      blockId,
    );
  }

  if (s && schema.format === 'url' && !isValidHttpOrHttpsUrl(s)) {
    return err('validation_block_invalid_url', { block: blockLabel, field: fieldPath, localeHint: '' }, blockId);
  }

  return null;
}

function validateField(
  schema: JsonSchemaField,
  value: unknown,
  fieldPath: string,
  locales: SupportedLocales[],
  blockLabel: string,
  blockId?: string,
): TContentBlockValidationError | null {
  if (schema.type === 'string') {
    if (schema.translatable) {
      return validateTranslatableString(schema, value, fieldPath, blockLabel, locales, blockId);
    }
    return validatePlainString(schema, value, fieldPath, blockLabel, blockId);
  }

  if (schema.type === 'object' && schema.properties) {
    const optionalNullable = schema.nullable === true && schema.required === false;
    if (optionalNullable && (value === undefined || value === null)) {
      return null;
    }

    if (schema.required === true && (value === undefined || value === null)) {
      return err('validation_block_field_required', { block: blockLabel, field: fieldPath }, blockId);
    }

    if (!schema.required && !optionalNullable && (value === undefined || value === null)) {
      return null;
    }

    if (value !== undefined && value !== null && (typeof value !== 'object' || Array.isArray(value))) {
      return err('validation_block_field_required', { block: blockLabel, field: fieldPath }, blockId);
    }

    const obj =
      value !== undefined && value !== null && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : {};
    // eslint-disable-next-line @typescript-eslint/no-use-before-define -- mutual recursion
    return validateObjectProperties(schema.properties, obj, fieldPath, locales, blockLabel, blockId);
  }

  if (schema.type === 'array' && schema.items) {
    const items = schema.items as JsonSchemaField | Record<string, JsonSchemaField>;

    if (
      schema.translatable &&
      typeof items === 'object' &&
      'type' in items &&
      (items as JsonSchemaField).type === 'string'
    ) {
      const required = schema.required === true;
      if (!required && (value === undefined || value === null)) {
        return null;
      }
      if (required) {
        if (!isLocaleStringArrayMap(value, locales)) {
          const loc = locales[0];
          return err(
            'validation_block_list_locale_required',
            { block: blockLabel, field: fieldPath, locale: (loc ?? 'UK').toUpperCase() },
            blockId,
          );
        }
        const m = value as Record<string, string[]>;
        for (const loc of locales) {
          const arr = m[loc] ?? [];
          if (!Array.isArray(arr) || arr.length === 0) {
            return err(
              'validation_block_list_locale_required',
              { block: blockLabel, field: fieldPath, locale: loc.toUpperCase() },
              blockId,
            );
          }
          for (const line of arr) {
            if (typeof line !== 'string' || !line.trim()) {
              return err(
                'validation_block_list_empty_line',
                { block: blockLabel, field: fieldPath, locale: loc.toUpperCase() },
                blockId,
              );
            }
          }
        }
        return null;
      }
      if (isLocaleStringArrayMap(value, locales)) {
        const m = value as Record<string, string[]>;
        for (const loc of locales) {
          const arr = m[loc] ?? [];
          for (const line of arr) {
            if (typeof line === 'string' && line.trim() === '') {
              return err(
                'validation_block_list_empty_line',
                { block: blockLabel, field: fieldPath, locale: loc.toUpperCase() },
                blockId,
              );
            }
          }
        }
      }
      return null;
    }

    if (typeof items === 'object' && 'type' in items && (items as JsonSchemaField).type === 'string') {
      const itemSch = items as JsonSchemaField;
      const arr = Array.isArray(value) ? (value as unknown[]) : [];
      if (schema.required === true && arr.length === 0) {
        return err('validation_block_array_required', { block: blockLabel, field: fieldPath }, blockId);
      }
      for (const el of arr) {
        if (typeof el !== 'string' || !el.trim()) {
          return err('validation_block_array_invalid_item', { block: blockLabel, field: fieldPath }, blockId);
        }
        const t = (el as string).trim();
        if (itemSch.format === 'uuid' && !isUuid(t)) {
          return err('validation_block_invalid_uuid', { block: blockLabel, field: fieldPath }, blockId);
        }
      }
      return null;
    }

    if (isObjectMapItems(items)) {
      const itemMap = items;
      const arr = Array.isArray(value) ? (value as unknown[]) : [];
      if (schema.required === true && arr.length === 0) {
        return err('validation_block_array_required', { block: blockLabel, field: fieldPath }, blockId);
      }
      for (let i = 0; i < arr.length; i++) {
        const row = arr[i];
        if (!row || typeof row !== 'object' || Array.isArray(row)) {
          return err('validation_block_array_invalid_item', { block: blockLabel, field: fieldPath }, blockId);
        }
        // eslint-disable-next-line @typescript-eslint/no-use-before-define -- mutual recursion
        const nested = validateObjectProperties(
          itemMap,
          row as Record<string, unknown>,
          `${fieldPath}[${i}]`,
          locales,
          blockLabel,
          blockId,
        );
        if (nested) return nested;
      }
      return null;
    }
  }

  return null;
}

function validateObjectProperties(
  properties: Record<string, JsonSchemaField>,
  value: Record<string, unknown>,
  pathPrefix: string,
  locales: SupportedLocales[],
  blockLabel: string,
  blockId?: string,
): TContentBlockValidationError | null {
  for (const [key, sch] of Object.entries(properties)) {
    if (key.startsWith('_')) continue;
    const fieldPath = pathPrefix ? `${pathPrefix}.${key}` : key;
    const fieldErr = validateField(sch, value[key], fieldPath, locales, blockLabel, blockId);
    if (fieldErr) return fieldErr;
  }
  return null;
}

function validateRootData(
  root: JsonSchemaField,
  data: unknown,
  locales: SupportedLocales[],
  blockLabel: string,
  blockId?: string,
): TContentBlockValidationError | null {
  if (root.type === 'array') {
    const itemMap = getArrayItemFieldMap(root);
    if (!itemMap) {
      return null;
    }
    const arr = Array.isArray(data) ? data : [];
    if (root.max_items !== undefined && arr.length > root.max_items) {
      return err('validation_block_max_items', { block: blockLabel, max: root.max_items }, blockId);
    }
    for (let i = 0; i < arr.length; i++) {
      const row = arr[i];
      if (!row || typeof row !== 'object' || Array.isArray(row)) {
        return err('validation_block_array_invalid_item', { block: blockLabel, field: `[${i}]` }, blockId);
      }
      const o = { ...(row as Record<string, unknown>) };
      delete o._rowId;
      const nested = validateObjectProperties(itemMap, o, `[${i}]`, locales, blockLabel, blockId);
      if (nested) return nested;
    }
    return null;
  }

  if (root.type === 'object' && root.properties) {
    const obj = data && typeof data === 'object' && !Array.isArray(data) ? (data as Record<string, unknown>) : {};
    return validateObjectProperties(root.properties, obj, '', locales, blockLabel, blockId);
  }

  return null;
}

/**
 * Validates merged block data (same shape as getInspectorBlock) against the normalized schema root.
 */
export function validateMergedBlockData(
  data: unknown,
  schemaRoot: ReturnType<typeof normalizeRootSchema>,
  locales: SupportedLocales[],
  blockLabel: string,
  blockId?: string,
): TContentBlockValidationError | null {
  return validateRootData(schemaRoot, data, locales, blockLabel, blockId);
}

export function validatePageContentBlocks(
  page: IContentPage,
  schemaByType: Record<string, IContentBlockSchemaItem>,
  locales: SupportedLocales[],
): TContentBlockValidationError | null {
  if (!page.localeBlocks || !page.blocks?.length) {
    return null;
  }

  for (let i = 0; i < page.blocks.length; i++) {
    const block = page.blocks[i];
    const item = schemaByType[block.type];
    if (!item?.schema) {
      continue;
    }

    const merged = getInspectorBlock(block, i, page.localeBlocks, locales, item.schema);
    const root = normalizeRootSchema(item.schema);
    const label = item.label || block.type;
    const e = validateMergedBlockData(merged.data, root, locales, label, block.id);
    if (e) {
      return { ...e, blockId: block.id };
    }
  }

  return null;
}
